import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { StockProvider } from "@/context/StockContext";
import { CatalogProvider } from "@/context/CatalogContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import Gate from "@/components/Gate";
import EliteInvite, { eliteInviteSeen } from "@/components/EliteInvite";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import TrafficTracker from "@/components/TrafficTracker";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import FAQ from "@/pages/FAQ";
import ProductDetail from "@/pages/ProductDetail";
import Admin from "@/pages/Admin";
import AdminReset from "@/pages/AdminReset";
import Track from "@/pages/Track";
import Reorder from "@/pages/Reorder";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Policy from "@/pages/Policy";
import Standards from "@/pages/Standards";
import Account from "@/pages/Account";
import Ambassador from "@/pages/Ambassador";
import Documentation from "@/pages/Documentation";
import BatchVerify from "@/pages/BatchVerify";
import About from "@/pages/About";
import ResearchLibrary from "@/pages/ResearchLibrary";
import ResearchArticle from "@/pages/ResearchArticle";
import { HelmetProvider } from "react-helmet-async";
import { useCart } from "@/context/CartContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reads ?ref=CODE from the URL: counts the click (once per browser per code) and silently applies the discount.
function RefCapture() {
  const { applyPromo } = useCart();
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("ref");
    if (!raw) return;
    const ref = raw.toUpperCase();
    const clickKey = `mx_ref_click_${ref}`;
    if (!localStorage.getItem(clickKey)) {
      localStorage.setItem(clickKey, "1");
      axios.post(`${API}/ambassadors/click`, { code: ref }).catch(() => {});
    }
    if (localStorage.getItem("mx_ref_applied") === ref) return;
    applyPromo(ref, { silent: true }).then((ok) => {
      if (ok) {
        localStorage.setItem("mx_ref_applied", ref);
        toast.success(`Referral code ${ref} applied — discount added at checkout`);
      }
    });
  }, [applyPromo]);
  return null;
}

// Returning visitors who already attested skip the catalog gate.
const hasEntered = () => {
  try { return !!localStorage.getItem("mx_attested"); }
  catch (e) { return false; }
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Handles the return trip from Emergent Google OAuth. It exchanges the one-time
// session_id (in the URL fragment) for a persistent session cookie, then reloads
// into /account so all providers (auth + gate) pick up the signed-in state.
function AuthCallback() {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) { window.location.replace("/account"); return; }
    axios.post(`${API}/account/session`, {}, { headers: { "X-Session-ID": sid }, withCredentials: true })
      .then(({ data }) => {
        try { localStorage.setItem("mx_email", data.email); } catch (e) { /* ignore */ }
        window.location.replace("/account");
      })
      .catch(() => { window.location.replace("/account?auth=failed"); });
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-[#D4AF37] font-mono text-xs uppercase tracking-[0.3em] animate-pulse" data-testid="auth-callback">
      Signing you in…
    </div>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [entered, setEntered] = useState(hasEntered);
  const [showEliteInvite, setShowEliteInvite] = useState(false);

  // The RUO attestation gates the research catalog only — public pages browse freely.
  const isCatalogRoute = pathname.startsWith("/catalog") || pathname.startsWith("/product");
  const gated = !entered && isCatalogRoute && !isAdmin;

  useEffect(() => {
    if (gated) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [gated]);

  return (
    <>
      <Toaster
        position={isAdmin ? "bottom-center" : "top-center"}
        theme="dark"
        offset={isAdmin ? 40 : 88}
        duration={isAdmin ? 6000 : 4000}
        style={{ zIndex: 100000 }}
        toastOptions={{ style: { background: "#0C0C0C", border: "1px solid #27272A", color: "#F5F5F0", borderRadius: 0 } }}
      />
      {gated && <Gate onComplete={() => { setEntered(true); if (!eliteInviteSeen()) setShowEliteInvite(true); }} />}
      {showEliteInvite && <EliteInvite onClose={() => setShowEliteInvite(false)} />}
      <ScrollToTop />
      <CartProvider>
        <RefCapture />
        <StockProvider>
          <CatalogProvider>
          <Header />
          <CartSheet />
          {!isAdmin && <ChatWidget />}
          <TrafficTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/standards" element={<Standards />} />
            <Route path="/about" element={<About />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/verify" element={<BatchVerify />} />
            <Route path="/library" element={<ResearchLibrary />} />
            <Route path="/library/:slug" element={<ResearchArticle />} />
            <Route path="/account" element={<Account />} />
            <Route path="/ambassador" element={<Ambassador />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/track" element={<Track />} />
            <Route path="/reorder" element={<Reorder />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/contact" element={<Policy />} />
            <Route path="/policy/:slug" element={<Policy />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/reset" element={<AdminReset />} />
          </Routes>
          <Footer />
          </CatalogProvider>
        </StockProvider>
      </CartProvider>
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process the OAuth return synchronously (before Gate/route logic) to avoid races.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <Shell />;
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <HelmetProvider>
        <BrowserRouter>
          <CustomerAuthProvider>
            <AppRouter />
          </CustomerAuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
